import { proxyCustomElement, HTMLElement, Build, h, Host } from '@stencil/core/internal/client';
import { i as isStr, a as inheritAttributes, g as getUrl } from './index.js';

const validateContent = (svgContent) => {
    const div = document.createElement('div');
    div.innerHTML = svgContent;
    // configure desta forma para garantir que funcione no nosso amigo IE
    for (let i = div.childNodes.length - 1; i >= 0; i--) {
        if (div.childNodes[i].nodeName.toLowerCase() !== 'svg') {
            div.removeChild(div.childNodes[i]);
        }
    }
    // deve ter apenas 1 elemento raiz
    const svgElm = div.firstElementChild;
    if (svgElm && svgElm.nodeName.toLowerCase() === 'svg') {
        const svgClass = svgElm.getAttribute('class') || '';
        svgElm.setAttribute('class', (svgClass + ' s-bsds-icon').trim());
        // o elemento raiz deve ser um svg
        // vamos verificar novamente se temos elementos válidos
        // não permitir scripts
        if (isValid(svgElm)) {
            return div.innerHTML;
        }
    }
    return '';
};
const isValid = (elm) => {
    if (elm.nodeType === 1) {
        if (elm.nodeName.toLowerCase() === 'script') {
            return false;
        }
        for (let i = 0; i < elm.attributes.length; i++) {
            const name = elm.attributes[i].name;
            if (isStr(name) && name.toLowerCase().indexOf('on') === 0) {
                return false;
            }
        }
        for (let i = 0; i < elm.childNodes.length; i++) {
            if (!isValid(elm.childNodes[i])) {
                return false;
            }
        }
    }
    return true;
};
const isSvgDataUrl = (url) => url.startsWith('data:image/svg+xml');
const isEncodedDataUrl = (url) => url.indexOf(';utf8,') !== -1;

const bsdsiconContent = new Map();
const requests = new Map();
let parser;
const getSvgContent = (url, sanitize) => {
    // veja se já temos uma solicitação para esta url
    let req = requests.get(url);
    if (!req) {
        if (typeof fetch !== 'undefined' && typeof document !== 'undefined') {
            /**
             * Se a url for uma url de dados de um svg, tente analisá-la
             * com o DOMParser. Isso funciona com políticas de segurança de conteúdo habilitadas.
             */
            if (isSvgDataUrl(url) && isEncodedDataUrl(url)) {
                if (!parser) {
                    /**
                     * Crie uma instância do parser DOM. Isso cria uma única
                     * instância do parser para o aplicativo inteiro, o que é mais eficiente.
                     */
                    parser = new DOMParser();
                }
                const doc = parser.parseFromString(url, 'text/html');
                const svg = doc.querySelector('svg');
                if (svg) {
                    bsdsiconContent.set(url, svg.outerHTML);
                }
                return Promise.resolve();
            }
            else {
                // ainda não temos uma solicitação
                req = fetch(url).then((rsp) => {
                    if (rsp.ok) {
                        return rsp.text().then((svgContent) => {
                            if (svgContent && sanitize !== false) {
                                svgContent = validateContent(svgContent);
                            }
                            bsdsiconContent.set(url, svgContent || '');
                        });
                    }
                    bsdsiconContent.set(url, '');
                });
                // cache para as mesmas solicitações
                requests.set(url, req);
            }
        }
        else {
            // definido como vazio para cenários ssr e resolver promise
            bsdsiconContent.set(url, '');
            return Promise.resolve();
        }
    }
    return req;
};

const iconCss = ":host{display:inline-block;width:1em;height:1em;contain:size layout style;-webkit-box-sizing:content-box !important;box-sizing:content-box !important;font-size:24px;-ms-flex-negative:0;flex-shrink:0}:host .bsdsicon{fill:currentColor;stroke:currentColor;stroke-width:0px}.bsdsicon-fill-none{fill:none}.icon-inner,.bsdsicon,svg{display:block;height:100%;width:100%}:host(.bsds-color){color:var(--bsds-color-content) !important}:host(.bsds-color-primary){--bsds-color-content:var(--bsds-color-content, #cc092f)}:host(.bsds-color-secondary){--bsds-color-content:var(--bsds-color-content, #0061AA)}:host(.bsds-color-success){--bsds-color-content:var(--bsds-color-content, #0A7437)}:host(.bsds-color-warning){--bsds-color-content:var(--bsds-color-content, #97771D)}:host(.bsds-color-danger){--bsds-color-content:var(--bsds-color-content, #cc092f)}:host(.bsds-color-info){--bsds-color-content:var(--bsds-color-content, #0061AA)}";

const Icon = /*@__PURE__*/ proxyCustomElement(class Icon extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.inheritedAttributes = {};
        this.didLoadIcon = false;
        this.isVisible = false;
        /**
         * Se habilitado, o bsds-icon será carregado lentamente quando estiver visível na janela de visualização.
         * Default, `false`.
         */
        this.lazy = false;
    }
    componentDidLoad() {
        /**
         * Resolve um problema do Angular em que os valores de propriedade são atribuídos após o 'connectedCallback', mas antes do registro dos observadores.
         * Este aprimoramento garante o carregamento de um ícone quando o componente tiver terminado a renderização e o ícone ainda não tiver aplicado os dados SVG.
         * Esta modificação diz respeito ao uso da sintaxe de vinculação do Angular:
         * `<bsds-icon [name]="nomeIcone"></bsds-icon>`
         */
        if (!this.didLoadIcon) {
            this.loadIcon();
        }
    }
    componentWillLoad() {
        this.inheritedAttributes = inheritAttributes(this.el, ['aria-label', 'aria-hidden']);
    }
    connectedCallback() {
        this.waitUntilVisible(this.el, '24px', () => {
            this.isVisible = true;
            this.loadIcon();
        });
    }
    disconnectedCallback() {
        if (this.io) {
            this.io.disconnect();
            this.io = undefined;
        }
    }
    waitUntilVisible(el, rootMargin, cb) {
        if (Build.isBrowser && this.lazy && typeof window !== 'undefined' && window.IntersectionObserver) {
            const io = (this.io = new window.IntersectionObserver((data) => {
                if (data[0].isIntersecting) {
                    io.disconnect();
                    this.io = undefined;
                    cb();
                }
            }, { rootMargin }));
            io.observe(el);
        }
        else {
            cb();
        }
    }
    loadIcon() {
        if (Build.isBrowser && this.isVisible) {
            const url = getUrl(this);
            if (url) {
                const sanitize = true;
                getSvgContent(url, sanitize).then(() => {
                    this.svgContent = bsdsiconContent.get(url);
                });
            }
        }
    }
    render() {
        const { inheritedAttributes } = this;
        return (h(Host, Object.assign({ key: 'b15eb9687e6652e3e5212e901e758eee3a09fe14', role: "img", "aria-hidden": "true", class: Object.assign({}, createColorClasses(this.color)) }, inheritedAttributes), Build.isBrowser && this.svgContent ? (h("div", { class: "icon-inner", innerHTML: this.svgContent })) : (h("div", { class: "icon-inner" }))));
    }
    static get assetsDirs() { return ["svg"]; }
    get el() { return this; }
    static get watchers() { return {
        "name": ["loadIcon"],
        "icon": ["loadIcon"]
    }; }
    static get style() { return iconCss; }
}, [1, "bsds-icon", {
        "color": [1],
        "name": [513],
        "icon": [520],
        "lazy": [4],
        "svgContent": [32],
        "isVisible": [32]
    }, undefined, {
        "name": ["loadIcon"],
        "icon": ["loadIcon"]
    }]);
const createColorClasses = (color) => {
    return color
        ? {
            'bsds-color': true,
            [`bsds-color-${color}`]: true,
        }
        : null;
};
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["bsds-icon"];
    components.forEach(tagName => { switch (tagName) {
        case "bsds-icon":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, Icon);
            }
            break;
    } });
}

const BsdsIcon = Icon;
const defineCustomElement = defineCustomElement$1;

export { BsdsIcon, defineCustomElement };
